
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import Amplify from 'aws-amplify';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import '../styles/global.scss';
import reducer from '../redux';
import { ComponentLookupContextProvider } from '../ComponentLookupContext';
import { CourseTopicContextProvider } from '../CourseTopicContext';
import i18nConfig from '../../i18n'
import Head from 'next/head';
import { useCookies } from "react-cookie";
import { loadPersonalizationAttributes } from '../redux/modules/personalization';
import useTranslation from 'next-translate/useTranslation';
import { getLocaleFromApi, setLocaleToApi } from '../utils/localeUtils';

import {
  createGrowthBookInstance,
  initializeGrowthBook,
  setGrowthBookAttributes
} from '../utils/growthbook';

import mapping from '../utils/mappings';

import pathsData from '../buildArtifacts/paths';
import tealium from '../utils/tealium';

const { pageCourseTopicNested: paths } = pathsData;

Amplify.configure({
  Auth: {
    identityPoolId: process.env.IDENTITY_POOL_ID,
    region: process.env.COGNITO_REGION,
    userPoolId: process.USER_POOL_ID,
    userPoolWebClientId: process.USER_POOL_ID
  },
  API: {
    endpoints: [
      {
        name: process.env.ENDPOINT_NAME,
        endpoint: process.env.ENDPOINT_URL,
        region: process.env.ENDPOINT_REGION
      }
    ]
  },
  aws_appsync_graphqlEndpoint: process.env.APPSYNC_GRAPHQL_ENDPOINT,
  aws_appsync_region: process.env.APPSYNC_REGION,
  aws_appsync_authenticationType: process.env.APPSYNC_AUTHENTICATION_TYPE
});

function MyApp({ Component, pageProps, err }) {
  const router = useRouter();
  const dispatch = useDispatch();

  // GrowthBook setup - Track initialization to prevent double attribute setting
  const [growthbook, setGrowthbook] = useState(() => {
    // Initialize with a working GrowthBook instance immediately to prevent provider errors
    if (typeof window !== 'undefined') {
      return createGrowthBookInstance();
    }
    return null;
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const personalization = useSelector((state) => state.personalization || {});

  // Load personalization data first
  useEffect(() => {
    dispatch(loadPersonalizationAttributes());
  }, [dispatch]);

  // INITIALIZE ONCE: Configure the existing GrowthBook instance once personalization is loaded
  useEffect(() => {
    if (!personalization.loaded || !growthbook || isInitialized) return;

    // Set initial attributes and initialize in sequence using existing instance
    setGrowthBookAttributes(growthbook, personalization)
      .then(() => {
        return initializeGrowthBook(growthbook);
      })
      .then(() => {
        setIsInitialized(true); // Mark as initialized to prevent double setting

        // Expose to window for GrowthBook extension debugging
        if (typeof window !== 'undefined') {
          window._growthbook = growthbook;
        }
      })
      .catch((error) => {
        console.error('[GrowthBook] Failed to initialize:', error);
      });
  }, [personalization.loaded, growthbook, isInitialized]);

  // UPDATE ATTRIBUTES ONLY: Never call init() again, just update attributes
  // Skip during initial setup to prevent double evaluation
  useEffect(() => {
    if (!growthbook || !personalization.loaded || !isInitialized) return;

    setGrowthBookAttributes(growthbook, personalization)
      .then(() => {
        // CRITICAL: Refresh to re-evaluate experiments with new attributes  
        try {
          if (growthbook && typeof growthbook.refresh === 'function') {
            growthbook.refresh();
          } else {
            if (growthbook && typeof growthbook.loadFeatures === 'function') {
              growthbook.loadFeatures().then(() => {
              }).catch(err => {
              });
            } else {
            }
          }
        } catch (refreshError) {
        }
      })
      .catch((error) => {
        console.error('[GrowthBook] Failed to update attributes:', error);
      });
  }, [
    growthbook, // Include growthbook to prevent race condition
    isInitialized, // Prevent double evaluation during initial setup
    personalization.public_user_id,
    personalization.accountType,
    personalization.productType,
    personalization.country
  ]);


  const { t, lang } = useTranslation();
  useEffect(() => {
    const consentCookieName = "__Secure-dbx_consent";
    const hasCookie = (name) => {
      return document.cookie
        .split(";")
        .map((cookie) => cookie.trim())
        .some((cookie) => cookie.startsWith(name + "="));
    };

    const waitForCookie = ({
      name,
      callback,
      interval = 1000,
      maxAttempts = 30,
    }) => {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts++;

        if (hasCookie(name)) {
          clearInterval(timer);
          callback();
        } else if (attempts >= maxAttempts) {
          clearInterval(timer);
          console.warn(`:warning: Cookie ${name} not found after ${maxAttempts} attempts.`);
        }
      }, interval);
    };

    waitForCookie({
      name: consentCookieName,
      callback: tealium,
    });
  }, []);
  useEffect(() => {
    if (window.coveoua) {
      window.coveoua("init", process.env.ANALYTICS_API_KEY, process.env.ANALYTICS_ENDPOINT);
      coveoua("send", "pageview");
    }
    const timer = setTimeout(() => {
      const exisitingiframe = document.getElementById("ccpa-iframe")
      const exisitingScript = document.querySelector("script[src*='privacy_consent.bundle']");
      let scriptUrl;

      if (exisitingScript) {
        scriptUrl = exisitingScript.src.split("?")[0]
        exisitingScript.remove()
      }
      if (!scriptUrl && window.dbxPithosConfig?._alreadyLoaded) {
        const found = Object.keys(window.dbxPithosConfig._alreadyLoaded)
          .find(k => k.includes("privacy_consent.bundle"));
        if (found) {
          scriptUrl = found.split("?")[0];
        }
      }
      if (exisitingiframe) {
        exisitingiframe.remove()
      }
      else {
        document.querySelectorAll("iframe[src*='ccpa_iframe']").forEach(el => {
          el.remove();
        });
      }
      if (scriptUrl) {
        const script = document.createElement("script");
        script.src = `${scriptUrl}?v=${Date.now()}`;
        script.defer = true;
        document.body.appendChild(script)
      }

    }, 2000)
    return () => clearTimeout(timer)

  }, [router.asPath]);
  useEffect(() => {
    if (pageProps && Object.keys(pageProps).length === 0) {
      return;
    }
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav && nav?.type === "back_forward") {
      return;
    }
    if (["fr", "de", "it", "ja", "es", 'pt-BR', 'pt-br'].includes(router.locale)) {

      return;
    }

    const fetchInitialLocale = async () => {
      const apiLocale = await getLocaleFromApi();
      const localeSet = ["fr", "de", "it", "ja", "es"].includes(apiLocale);
      if (router && apiLocale !== lang && localeSet) {
        const cleanPath = router.asPath.replace(/[?&]fallback=true/g, '').replace(/[?&]$/, '');
        router.push(cleanPath, undefined, { locale: apiLocale });
      }
      else if (apiLocale === 'en') {
        const cleanPath = router.asPath.replace(/[?&]fallback=true/g, '').replace(/[?&]$/, '');
        router.push(cleanPath, undefined, { locale: 'en-US' });
      }
      else if (apiLocale === 'pt_BR') {
        const cleanPath = router.asPath.replace(/[?&]fallback=true/g, '').replace(/[?&]$/, '');
        router.push(cleanPath, undefined, { locale: 'pt-br' });

      }
      else if (!localeSet) {
        let cleanPath = router.asPath.replace(/([?&])fallback=true(&|$)/, '').replace(/[?&]$/, '');
        if (!cleanPath.includes('fallback=true')) {
          cleanPath += (cleanPath.includes('?') ? '&' : '?') + 'fallback=true';
        }
        router.push(cleanPath, undefined, { locale: 'en-US' });
      }
    }
    if (router.pathname !== "/preview") {
      fetchInitialLocale();
    }
  }, []);
  useEffect(async()=>{
    const success = await setLocaleToApi(router.locale);
  },[router.locale])
  return (
    <>
      <Head>
        {i18nConfig && i18nConfig?.locales.length > 0 &&
          i18nConfig?.locales.map((lang, index) => (
            <link
              key={lang || index}
              rel="alternate-text"
              hrefLang={lang}
              href={`https://learn.dropbox.com/${lang}${router.asPath}`}>
            </link>
          ))}
      </Head>
      <GrowthBookProvider growthbook={growthbook}>
        <ComponentLookupContextProvider componentLookup={(contentType) => mapping[contentType]}>
          <CourseTopicContextProvider value={paths}>
            <Component {...pageProps} err={err} />
          </CourseTopicContextProvider>
        </ComponentLookupContextProvider>
      </GrowthBookProvider>
    </>
  );
}

const __Page_Next_Translate__ = reducer.withRedux(MyApp);


    export default __appWithI18n(__Page_Next_Translate__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      
    });
  